// v2.4.0 — Hook : fetch OSRM driving route entre user et dest, avec debounce 5s.
// Retourne { coords: [[lng,lat],…], distance_m, duration_s, status, error }.
// Fallback : si OSRM down/timeout → ligne droite (2 points) + status === 'fallback'.

import { useEffect, useRef, useState } from "react";

const OSRM_ENDPOINT = "https://router.project-osrm.org/route/v1/driving";
const DEBOUNCE_MS = 5000;
const TIMEOUT_MS = 6000;

function haversineMeters(aLat, aLng, bLat, bLng) {
  const R = 6371000;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x)));
}

export default function useOSRMRoute(userPos, destPos) {
  // userPos / destPos format : [lat, lng] (convention Leaflet du code existant).
  const [route, setRoute] = useState({
    coords: null,
    distance_m: null,
    duration_s: null,
    status: "idle",
    error: null,
  });
  const lastFetchRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!userPos || !destPos) return;

    const fetchRoute = async () => {
      const now = Date.now();
      lastFetchRef.current = now;
      const [uLat, uLng] = userPos;
      const [dLat, dLng] = destPos;
      const url = `${OSRM_ENDPOINT}/${uLng},${uLat};${dLng},${dLat}?overview=full&geometries=geojson&steps=true`;

      const ctrl = new AbortController();
      const tid = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
      try {
        const r = await fetch(url, { signal: ctrl.signal });
        clearTimeout(tid);
        if (!r.ok) throw new Error(`osrm http ${r.status}`);
        const data = await r.json();
        const leg = data.routes && data.routes[0];
        if (!leg || !leg.geometry || !Array.isArray(leg.geometry.coordinates)) {
          throw new Error("osrm empty route");
        }
        // v3.6.5 — Manœuvres OSRM (turn-by-turn) : flèche + « tourne à droite dans 200 m ».
        let steps = [];
        try {
          steps = (leg.legs?.[0]?.steps || []).map((s) => ({
            loc: s.maneuver?.location || null,           // [lng, lat]
            type: s.maneuver?.type || "turn",
            modifier: s.maneuver?.modifier || null,
            exit: s.maneuver?.exit || null,
            name: s.name || null,
          })).filter((s) => Array.isArray(s.loc));
        } catch (_) { steps = []; }
        setRoute({
          coords: leg.geometry.coordinates, // [[lng,lat],…]
          distance_m: Math.round(leg.distance),
          duration_s: Math.round(leg.duration),
          steps,
          status: "ok",
          error: null,
        });
      } catch (err) {
        clearTimeout(tid);
        // Fallback : ligne droite
        const d = haversineMeters(uLat, uLng, dLat, dLng);
        setRoute({
          coords: [[uLng, uLat], [dLng, dLat]],
          distance_m: d,
          duration_s: Math.round((d / 1000 / 25) * 3600), // 25 km/h moto urbaine
          steps: [],
          status: "fallback",
          error: err.message || "osrm error",
        });
      }
    };

    // Debounce 5s
    if (timerRef.current) clearTimeout(timerRef.current);
    const elapsed = Date.now() - lastFetchRef.current;
    const delay = lastFetchRef.current === 0 ? 0 : Math.max(0, DEBOUNCE_MS - elapsed);
    timerRef.current = setTimeout(fetchRoute, delay);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [userPos && userPos[0], userPos && userPos[1], destPos && destPos[0], destPos && destPos[1]]); // eslint-disable-line

  return route;
}

export { haversineMeters };
